import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// --- API helpers (kept out of the hook to separate I/O from state logic) ---
async function fetchTrending() {
  const res = await axios.get(`${API}/searches/trending?limit=6`);
  return res.data.trending || [];
}

async function fetchResults(query) {
  const res = await axios.get(`${API}/search`, { params: { q: query } });
  return (res.data.results || []).map((r) => ({ ...r, isHack: r.is_hack }));
}

// Encapsulates query state, debouncing, and backend search + trending fetches.
export default function useDomainSearch(debounceMs = 280) {
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [trending, setTrending] = useState([]);

  // Fetch trending searches once on mount.
  useEffect(() => {
    let active = true;
    fetchTrending()
      .then((t) => active && setTrending(t))
      .catch(() => active && setTrending([]));
    return () => {
      active = false;
    };
  }, []);

  // Debounce the raw query.
  useEffect(() => {
    const trimmed = query.trim();
    if (!trimmed) {
      setDebounced("");
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const timer = setTimeout(() => setDebounced(trimmed), debounceMs);
    return () => clearTimeout(timer);
  }, [query, debounceMs]);

  // Fetch results whenever the debounced query changes.
  useEffect(() => {
    if (!debounced) {
      setResults([]);
      return undefined;
    }
    let active = true;
    fetchResults(debounced)
      .then((r) => active && setResults(r))
      .catch(() => active && setResults([]))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [debounced]);

  return { query, setQuery, debounced, results, loading, trending };
}
